/**
 * Orchestration Tab
 * Create and execute multi-agent execution plans using ERC-8004 agents
 */

import { useState } from 'react';
import { Search, Network, Loader2, Bot } from 'lucide-react';
import { useAccount, useSwitchChain, useConfig } from 'wagmi';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorMessage } from '../shared/ErrorMessage';
import { ExecutionPlanDisplay } from '../execution/ExecutionPlanDisplay';
import { ExecutionResultsDisplay } from '../execution/ExecutionResultsDisplay';
import { CHAIN_INFO, type ChainId } from '@/config/wagmi';
import type { TenantCredentials } from '@/types/database';

const AGENT_SERVICE_URL = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:8080';

type ViewState = 'search' | 'plan' | 'executing' | 'results';

interface ExecutionPlanResponse {
  query: string;
  query_id: string;
  capability_analysis: any;
  execution_plans: any[];
  recommended_plan: any;
  payment_details: any;
  dry_run: string;
}

interface OrchestrationTabProps {
  credentials: TenantCredentials | null;
  onProvision: () => void;
}

export function OrchestrationTab({ credentials, onProvision }: OrchestrationTabProps) {
  const { address, isConnected, chain } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const config = useConfig();

  const [searchQuery, setSearchQuery] = useState('');
  const [maxBudget, setMaxBudget] = useState(0.30);
  const [strategy, setStrategy] = useState<'greedy' | 'balanced' | 'quality'>('balanced');

  const [viewState, setViewState] = useState<ViewState>('search');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [executionPlan, setExecutionPlan] = useState<ExecutionPlanResponse | null>(null);
  const [executionResults, setExecutionResults] = useState<any | null>(null);
  const [executionProgress, setExecutionProgress] = useState<{
    currentStep: number;
    totalSteps: number;
    status: string;
  }>({ currentStep: 0, totalSteps: 0, status: '' });

  const exampleQueries = [
    'What agents analyze DeFi protocols?',
    'Find agents with smart contract auditing capabilities',
    'Which agents support blockchain analytics?',
    'Show me agents for NFT marketplaces',
    'Find governance and DAO-related agents',
  ];

  const handleGeneratePlan = async () => {
    if (!searchQuery.trim() || !credentials) return;

    setIsLoading(true);
    setError(null);

    try {
      // Call orchestration endpoint with user credentials
      const response = await fetch(`${AGENT_SERVICE_URL}/api/v1/kg/orchestrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_base: credentials.api_base,
          api_key: credentials.api_key,
          question: searchQuery,
          max_budget: maxBudget,
          strategy,
          dry_run: true, // Generate plan only
          user_wallet: address,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `Failed to generate plan: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate execution plan');
      }

      setExecutionPlan(result);
      setViewState('plan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('[Generate Plan Error]', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecutePlan = async () => {
    if (!executionPlan || !credentials) return;

    if (!isConnected || !address) {
      setError('Please connect your wallet to execute the plan');
      return;
    }

    setError(null);

    try {
      const steps = executionPlan.recommended_plan.steps;
      const requiredChainId = steps[0]?.wallet_chain_id || 84532;

      // Switch chain if needed
      if (chain?.id !== requiredChainId) {
        await switchChainAsync({ chainId: requiredChainId });
      }

      setViewState('executing');
      const stepResults = [];

      // Execute each step
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        setExecutionProgress({
          currentStep: i + 1,
          totalSteps: steps.length,
          status: `Preparing step ${i + 1}: ${step.task}`,
        });

        // Call the orchestration endpoint with dry_run=false
        const response = await fetch(`${AGENT_SERVICE_URL}/api/v1/kg/orchestrate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            api_base: credentials.api_base,
            api_key: credentials.api_key,
            question: step.task,
            max_budget: step.cost_usdc,
            strategy,
            dry_run: false,
            user_wallet: address,
          }),
        });

        const stepResult = await response.json();

        stepResults.push({
          stepId: step.step_id,
          agent: step.agent_name,
          capability: step.capability,
          result: stepResult,
          transactionHash: stepResult.transaction_hash || null,
          cost: step.cost_usdc,
        });
      }

      setExecutionResults({
        query: executionPlan.query,
        planId: executionPlan.recommended_plan.plan_id,
        steps: stepResults,
        totalCost: executionPlan.recommended_plan.total_cost_usdc,
        totalTime: executionPlan.recommended_plan.total_duration_sec,
      });
      setViewState('results');
    } catch (err) {
      console.error('[Execute Plan Error]', err);
      setError(err instanceof Error ? err.message : 'Execution failed');
      setViewState('plan');
    }
  };

  const handleNewQuery = () => {
    setSearchQuery('');
    setExecutionPlan(null);
    setExecutionResults(null);
    setViewState('search');
    setError(null);
  };

  // If no credentials, show provision prompt
  if (!credentials) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <div className="mb-6">
          <Network className="mx-auto h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Database Provisioned</h3>
        <p className="text-gray-600 mb-6">
          You need to provision a KnowledgeFlowDB database first to use orchestration.
        </p>
        <button
          onClick={onProvision}
          className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          Provision Database
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search View */}
      {viewState === 'search' && (
        <>
          {/* Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="h-5 w-5 text-blue-600 mt-0.5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-900">Multi-Agent Orchestration</h3>
                <p className="mt-1 text-sm text-blue-700">
                  Create execution plans using multiple ERC-8004 agents. The system analyzes your
                  query and selects the best agents to accomplish your task.
                </p>
              </div>
            </div>
          </div>

          {/* Query Form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <Network className="w-8 h-8 text-purple-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Create Execution Plan</h2>
                <p className="text-gray-600 mt-1">
                  Query your knowledge graph to create multi-agent workflows
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Query Input */}
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  What would you like to know?
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleGeneratePlan()}
                    placeholder="e.g., What agents analyze DeFi protocols?"
                    className="pl-10 w-full rounded-md border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg"
                  />
                </div>
              </div>

              {/* Parameters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                    Max Budget (USDC)
                  </label>
                  <input
                    id="budget"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={maxBudget}
                    onChange={(e) => setMaxBudget(parseFloat(e.target.value))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label htmlFor="strategy" className="block text-sm font-medium text-gray-700 mb-1">
                    Strategy
                  </label>
                  <select
                    id="strategy"
                    value={strategy}
                    onChange={(e) =>
                      setStrategy(e.target.value as 'greedy' | 'balanced' | 'quality')
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="greedy">Greedy (Lowest Cost)</option>
                    <option value="balanced">Balanced</option>
                    <option value="quality">Quality (Best Agents)</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={handleGeneratePlan}
                    disabled={isLoading || !searchQuery.trim()}
                    className="w-full px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating Plan...
                      </>
                    ) : (
                      <>
                        <Network className="h-4 w-4" />
                        Generate Plan
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Example Queries */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-3">Try these examples:</p>
              <div className="flex flex-wrap gap-2">
                {exampleQueries.map((query, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSearchQuery(query)}
                    className="text-sm px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && <ErrorMessage message={error} />}

          {/* Empty State */}
          {!error && (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Bot className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Execute Queries with AI Agents
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Enter a query above to generate an execution plan using your ERC-8004 agents from
                the knowledge graph.
              </p>
            </div>
          )}
        </>
      )}

      {/* Plan View */}
      {viewState === 'plan' && executionPlan && (
        <ExecutionPlanDisplay
          query={executionPlan.query}
          queryId={executionPlan.query_id}
          capabilityAnalysis={executionPlan.capability_analysis}
          executionPlans={executionPlan.execution_plans}
          recommendedPlan={executionPlan.recommended_plan}
          paymentDetails={executionPlan.payment_details}
          onExecute={handleExecutePlan}
          onCancel={() => setViewState('search')}
          currentChainId={chain?.id}
        />
      )}

      {/* Executing View */}
      {viewState === 'executing' && (
        <div className="bg-white rounded-lg shadow-sm p-12">
          <div className="text-center mb-6">
            <LoadingSpinner />
            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-2">
              Executing Step {executionProgress.currentStep} of {executionProgress.totalSteps}
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-4">
              {executionProgress.status || 'Calling agents and waiting for responses...'}
            </p>
          </div>

          {executionProgress.totalSteps > 0 && (
            <div className="max-w-md mx-auto">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${(executionProgress.currentStep / executionProgress.totalSteps) * 100}%`,
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Progress</span>
                <span>
                  {Math.round(
                    (executionProgress.currentStep / executionProgress.totalSteps) * 100
                  )}
                  %
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewState === 'results' && executionResults && (
        <ExecutionResultsDisplay
          result={executionResults}
          onClose={() => setViewState('search')}
          onNewQuery={handleNewQuery}
        />
      )}
    </div>
  );
}
